import type { Container } from "pixi.js";

/**
 * Shared input plumbing for the round overlays: `OverlayPressBinding` for their presenters (big win,
 * feature trigger, feature finished, the game's gamble presenter), `bindTap` for a view's tap surface
 * and the grid's slam surface. A "press" is a tap on the overlay's input surface OR a key on the
 * window that is not a modifier, a Ctrl / Alt / Meta chord or a repeat (`bindKeys`).
 */
/**
 * Bind a tap on `surface` → `onPress` (only while the surface is shown); returns the unbind. The
 * building block views use to implement `bindSkipTap()`-style port methods.
 */
export function bindTap(surface: Container, onPress: () => void): () => void {
  if (surface.eventMode === "auto" || surface.eventMode === "none") surface.eventMode = "static";
  surface.cursor = "pointer";
  const handler = (): void => {
    if (surface.visible) onPress();
  };
  surface.on("pointertap", handler);
  return () => {
    if (!surface.destroyed) surface.off("pointertap", handler);
  };
}

/**
 * The keys that are never a press on their own: the modifiers (the first keydown of Alt-Tab / Cmd-Tab
 * fires before the switch; `OSLeft` / `OSRight` are the Meta keys in older Firefox), Tab and CapsLock.
 * In offer mode a press declines and in the gamble it collects, so a window switch, a Shift or a
 * browser shortcut must not choose for the player. Every other key (Enter, Space, Escape, a letter,
 * an arrow, an F-key, a media key) is a press.
 */
const NON_PRESS_KEY_CODES: readonly string[] = [
  "ShiftLeft", "ShiftRight", "AltLeft", "AltRight", "ControlLeft", "ControlRight",
  "MetaLeft", "MetaRight", "OSLeft", "OSRight", "Tab", "CapsLock",
];

export class OverlayPressBinding {
  private readonly surfaces: Array<{ node: Container; handler: () => void }> = [];
  private keyHandler: ((e: KeyboardEvent) => void) | null = null;

  /** Bind a tap on `surface` (if given) → `onPress`. Ignored when the surface is hidden. */
  public bindTap(surface: Container | null | undefined, onPress: () => void): void {
    if (!surface || surface.destroyed) return;
    if (surface.eventMode === "auto" || surface.eventMode === "none") surface.eventMode = "static";
    surface.cursor = "pointer";
    const handler = (): void => onPress();
    surface.on("pointertap", handler);
    this.surfaces.push({ node: surface, handler });
  }

  /**
   * Bind window keydown → `onPress` for any key but a repeat, a Ctrl / Alt / Meta chord (a shortcut)
   * or one of `NON_PRESS_KEY_CODES`. `codes` limits it further to specific keys.
   */
  public bindKeys(onPress: () => void, codes?: readonly string[]): void {
    if (typeof window === "undefined") return;
    this.unbindKeys();
    this.keyHandler = (e: KeyboardEvent): void => {
      if (e.repeat || e.ctrlKey || e.altKey || e.metaKey || NON_PRESS_KEY_CODES.includes(e.code)) return;
      if (codes && !codes.includes(e.code)) return;
      onPress();
    };
    window.addEventListener("keydown", this.keyHandler, { passive: true });
  }

  public unbindAll(): void {
    for (const { node, handler } of this.surfaces) {
      if (!node.destroyed) node.off("pointertap", handler);
    }
    this.surfaces.length = 0;
    this.unbindKeys();
  }

  private unbindKeys(): void {
    if (this.keyHandler && typeof window !== "undefined") window.removeEventListener("keydown", this.keyHandler);
    this.keyHandler = null;
  }
}
